import { DriveStep } from 'driver.js';

/**
 * Tour step definitions for the Cashflow application onboarding
 * These steps guide new users through the main features
 */
export const tourSteps: DriveStep[] = [
  {
    popover: {
      title: 'Welcome to Cashflow! 👋',
      description:
        'Let\'s take a quick tour to help you get started with managing your cash flow forecasts. This will only take a minute!',
    },
  },
  {
    element: '#forecast-chart-section',
    popover: {
      title: 'Cashflow Forecast Chart 📊',
      description:
        'This chart visualizes your cash flow over time. The blue line shows your current forecast, and the green line shows anticipated projects. Watch your cash balance trend here!',
      side: 'bottom',
    },
  },
  {
    element: '#forecast-form',
    popover: {
      title: 'Set Your Forecast Period 📅',
      description:
        'Choose your start and end dates to define the forecast period. Click "Generate Forecast" to update the chart and tables with your selected date range.',
      side: 'bottom',
    },
  },
  {
    element: '#snapshots-panel',
    popover: {
      title: 'Save Snapshots 💾',
      description:
        'Save different forecast scenarios as snapshots. You can compare them later or restore a previous forecast state. Great for "what-if" analysis!',
      side: 'left',
    },
  },
  {
    element: '#chart-legend-panel',
    popover: {
      title: 'Chart Legend & Controls 🎨',
      description:
        'Toggle different data series on/off to focus on what matters. Click on any legend item to show or hide that data in the chart.',
      side: 'left',
    },
  },
  {
    element: '.ant-tabs-nav',
    popover: {
      title: 'Organize by Tabs 📑',
      description:
        'Switch between Current Items (confirmed work), Anticipated Items (potential work), Account Balances, and Fixed Expenses. We use "Items" instead of "Projects" because different businesses track different things - projects, clients, seasonal lines, etc.',
      side: 'bottom',
    },
  },
  {
    element: '.project-table-container',
    popover: {
      title: 'Items by Week 📋',
      description:
        'Your items are organized by week. Each row is an item (project, client, product line, etc.), and columns represent weeks. You can see all income and expenses broken down over time.',
      side: 'top',
    },
  },
  {
    element: '[title="Create New Item"]',
    popover: {
      title: 'Create New Item ➕',
      description:
        'Click this blue button to create a new Item (the main container for your work). To add payments, expenses, purchase orders, or invoices to an existing item, right-click on any week cell in that item\'s row.',
      side: 'bottom',
    },
  },
  {
    element: '[title="Table Settings"]',
    popover: {
      title: 'Table Settings ⚙️',
      description:
        'Access table options like clearing filters, showing archived items, and other table controls. Keep your view organized!',
      side: 'bottom',
    },
  },
  {
    popover: {
      title: 'Drag & Drop Cards 🖱️',
      description:
        'You can drag and drop payment cards, expense cards, purchase orders, and invoices between weeks to reschedule them. Just click and drag any card to a different week cell!',
    },
  },
  {
    popover: {
      title: 'Right-Click for Actions 🖱️',
      description:
        'Right-click on an item name to Edit, Copy, or Archive it. Right-click on any week cell to add payments, expenses, purchase orders, or invoices to that item for that week. You can also use the action buttons on each card.',
    },
  },
  {
    popover: {
      title: 'You\'re All Set! 🎉',
      description:
        'You now know the basics of Cashflow! Start by creating your first item or importing your accounts. Remember: the blue button creates items, right-click on weeks to add transactions. You can restart this tour anytime from the user menu. Happy forecasting!',
    },
  },
];

/**
 * Configuration for the tour driver
 */
export const tourConfig = {
  showProgress: true,
  showButtons: ['next' as const, 'previous' as const, 'close' as const],
  progressText: '{{current}} of {{total}}',
  nextBtnText: 'Next →',
  prevBtnText: '← Previous',
  doneBtnText: 'Done ✓',
  closeBtnText: '✕',
  allowClose: true,
  overlayClickNext: false,
  smoothScroll: true,
  animate: true,
  popoverClass: 'cashflow-tour-popover',
  onDestroyStarted: () => {
    // Allow closing the tour
    return true;
  },
};

